import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL, getAuthHeaders } from './dashboardUtils';
import { toast } from '../hooks/use-toast';
import { AlertTriangle, Trash2, RefreshCw, Users, Car, FileWarning } from 'lucide-react';

export default function DataIntegrityTab() {
  const [loading, setLoading] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [orphans, setOrphans] = useState([]);
  const [mismatches, setMismatches] = useState([]);
  const [activeSection, setActiveSection] = useState('duplicates');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [dupRes, orphanRes, mismatchRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/data-integrity/duplicates`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/data-integrity/orphan-vehicles`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/data-integrity/job-plate-mismatches`, { headers }),
      ]);
      const [dupData, orphanData, mismatchData] = await Promise.all([
        dupRes.json(), orphanRes.json(), mismatchRes.json()
      ]);
      setDuplicates(dupData.duplicates || []);
      setOrphans(orphanData.orphans || []);
      setMismatches(mismatchData.mismatches || []);
    } catch (e) {
      toast({ title: 'Hata', description: 'Veriler yüklenemedi', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const deleteCustomer = async (customerId, name) => {
    if (!window.confirm(`"${name}" müşterisini silmek istediğinize emin misiniz?`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/data-integrity/customer/${customerId}`, {
        method: 'DELETE', headers: getAuthHeaders()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Silinemedi');
      toast({ title: 'Silindi', description: `${name} silindi` });
      fetchAll();
    } catch (e) {
      toast({ title: 'Hata', description: e.message, variant: 'destructive' });
    }
  };

  const deleteOrphan = async (vehicleId, plate) => {
    if (!window.confirm(`"${plate}" plakalı aracı silmek istediğinize emin misiniz?`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/data-integrity/orphan-vehicle/${vehicleId}`, {
        method: 'DELETE', headers: getAuthHeaders()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Silinemedi');
      toast({ title: 'Silindi', description: `${plate} silindi` });
      fetchAll();
    } catch (e) {
      toast({ title: 'Hata', description: e.message, variant: 'destructive' });
    }
  };

  const sections = [
    { key: 'duplicates', label: 'Duplicate Müşteriler', icon: <Users size={15} />, count: duplicates.length },
    { key: 'orphans', label: 'Sahipsiz Araçlar', icon: <Car size={15} />, count: orphans.length },
    { key: 'mismatches', label: 'Plaka Uyuşmazlıkları', icon: <FileWarning size={15} />, count: mismatches.length },
  ];

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Veri Temizliği</h2>
        <button
          onClick={fetchAll}
          disabled={loading}
          className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border border-border hover:bg-muted transition-colors"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Yenile
        </button>
      </div>

      {/* Sekme Butonları */}
      <div className="flex gap-2 flex-wrap">
        {sections.map(s => (
          <button
            key={s.key}
            onClick={() => setActiveSection(s.key)}
            className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border transition-colors ${
              activeSection === s.key
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border hover:bg-muted'
            }`}
          >
            {s.icon}
            {s.label}
            {s.count > 0 && (
              <span className={`ml-1 text-xs font-bold px-1.5 py-0.5 rounded-full ${
                activeSection === s.key ? 'bg-white/20' : 'bg-destructive text-destructive-foreground'
              }`}>
                {s.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading && (
        <div className="text-sm text-muted-foreground py-8 text-center">Yükleniyor...</div>
      )}

      {/* Duplicate Müşteriler */}
      {!loading && activeSection === 'duplicates' && (
        <div className="space-y-3">
          {duplicates.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Duplicate müşteri bulunamadı ✓</div>
          ) : duplicates.map((group, i) => (
            <div key={i} className="border border-border rounded-lg overflow-hidden">
              <div className="bg-muted px-4 py-2 flex items-center gap-2 text-sm font-medium">
                <AlertTriangle size={14} className="text-amber-500" />
                <span>{group.reasonLabel}:</span>
                <span className="font-bold">{group.value}</span>
              </div>
              <div className="divide-y divide-border">
                {group.customers.map(c => (
                  <div key={c.id} className="flex items-center justify-between px-4 py-3">
                    <div>
                      <div className="font-medium text-sm">{c.name}</div>
                      <div className="text-xs text-muted-foreground">{c.email} · {c.phone}</div>
                      {c.bonusBalance > 0 && (
                        <div className="text-xs text-green-600">Bonus: {c.bonusBalance} TL</div>
                      )}
                    </div>
                    <button
                      onClick={() => deleteCustomer(c.id, c.name)}
                      className="flex items-center gap-1 text-xs text-destructive hover:bg-destructive/10 px-2 py-1 rounded transition-colors"
                    >
                      <Trash2 size={13} />
                      Sil
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sahipsiz Araçlar */}
      {!loading && activeSection === 'orphans' && (
        <div className="space-y-2">
          {orphans.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Sahipsiz araç bulunamadı ✓</div>
          ) : (
            <div className="border border-border rounded-lg divide-y divide-border">
              {orphans.map(v => (
                <div key={v.id} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <div className="font-medium text-sm">{v.plate || '(Plakasız)'}</div>
                    <div className="text-xs text-muted-foreground">{v.details || '—'}</div>
                  </div>
                  <button
                    onClick={() => deleteOrphan(v.id, v.plate || v.id)}
                    className="flex items-center gap-1 text-xs text-destructive hover:bg-destructive/10 px-2 py-1 rounded transition-colors"
                  >
                    <Trash2 size={13} />
                    Sil
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Plaka Uyuşmazlıkları */}
      {!loading && activeSection === 'mismatches' && (
        <div className="space-y-2">
          {mismatches.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Plaka uyuşmazlığı bulunamadı ✓</div>
          ) : (
            <div className="border border-border rounded-lg divide-y divide-border">
              {mismatches.map((m, i) => (
                <div key={i} className="px-4 py-3">
                  <div className="font-medium text-sm">{m.customerName}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    İşteki plaka: <span className="font-mono text-amber-600">{m.jobPlate || '—'}</span>
                    {' · '}
                    Araç kaydındaki: <span className="font-mono text-amber-600">{m.vehiclePlate || '—'}</span>
                  </div>
                  <div className="text-xs text-destructive mt-0.5">{m.issue}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
